/**
 * Units API
 *
 * All unit-related data access functions.
 * Uses RLS policies for authorization.
 */

import { supabase, normalizeError } from '@/lib/supabaseClient'

/**
 * Fetch all units for a project
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export async function fetchUnits(projectId) {
  try {
    const { data, error } = await supabase
      .from('units')
      .select(`
        *,
        unit_type:unit_types(*)
      `)
      .eq('project_id', projectId)
      .order('unit_number', { ascending: true })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Fetch single unit by ID
 *
 * @param {string} unitId - Unit UUID
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function fetchUnitById(unitId) {
  try {
    const { data, error } = await supabase
      .from('units')
      .select(`
        *,
        unit_type:unit_types(*)
      `)
      .eq('id', unitId)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Create a new unit
 *
 * @param {Object} unitData - Unit data
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function createUnit(unitData) {
  try {
    const { data, error } = await supabase
      .from('units')
      .insert(unitData)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Update existing unit
 *
 * @param {string} unitId - Unit UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function updateUnit(unitId, updates) {
  try {
    const { data, error } = await supabase
      .from('units')
      .update(updates)
      .eq('id', unitId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Delete unit
 *
 * @param {string} unitId - Unit UUID
 * @returns {Promise<{error: Object|null}>}
 */
export async function deleteUnit(unitId) {
  try {
    const { error } = await supabase
      .from('units')
      .delete()
      .eq('id', unitId)

    if (error) throw error
    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Assign unit type to unit (inherits unit type attributes)
 *
 * @param {string} unitId - Unit UUID
 * @param {string} unitTypeId - Unit Type UUID
 * @param {boolean} inheritAttributes - Whether to inherit attributes from unit type
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function assignUnitType(unitId, unitTypeId, inheritAttributes = true) {
  try {
    const updates = { unit_type_id: unitTypeId || null }

    if (unitTypeId && inheritAttributes) {
      const { data: unitType, error: typeError } = await supabase
        .from('unit_types')
        .select('*')
        .eq('id', unitTypeId)
        .single()

      if (typeError) throw typeError

      updates.bedrooms = unitType.bedrooms
      updates.bathrooms = unitType.bathrooms
      updates.garages = unitType.garages
      updates.car_parks = unitType.car_parks
      updates.sqm = unitType.internal_size_sqm
      if (unitType.default_sale_price) {
        updates.price = unitType.default_sale_price
      }
      updates.overridden_fields = {}
    }

    const { data, error } = await supabase
      .from('units')
      .update(updates)
      .eq('id', unitId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Update unit field and track override
 *
 * @param {string} unitId - Unit UUID
 * @param {string} fieldName - Field name to update
 * @param {any} value - New value
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function updateUnitField(unitId, fieldName, value) {
  try {
    const { data: unit, error: fetchError } = await supabase
      .from('units')
      .select('overridden_fields')
      .eq('id', unitId)
      .single()

    if (fetchError) throw fetchError

    const overridden = unit.overridden_fields || {}
    overridden[fieldName] = true

    const { data, error } = await supabase
      .from('units')
      .update({
        [fieldName]: value,
        overridden_fields: overridden,
      })
      .eq('id', unitId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Bulk assign unit type to multiple units
 *
 * @param {Array<string>} unitIds - Array of Unit UUIDs
 * @param {string} unitTypeId - Unit Type UUID
 * @param {boolean} inheritAttributes - Whether to inherit attributes
 * @returns {Promise<{error: Object|null}>}
 */
export async function bulkAssignUnitType(unitIds, unitTypeId, inheritAttributes = true) {
  try {
    const results = await Promise.all(
      unitIds.map(unitId => assignUnitType(unitId, unitTypeId, inheritAttributes))
    )

    const firstError = results.find(r => r.error)
    if (firstError?.error) throw firstError.error

    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Bulk update unit status
 *
 * @param {Array<string>} unitIds - Array of Unit UUIDs
 * @param {string} status - New status
 * @returns {Promise<{error: Object|null}>}
 */
export async function bulkUpdateUnitStatus(unitIds, status) {
  try {
    const { error } = await supabase
      .from('units')
      .update({ status })
      .in('id', unitIds)

    if (error) throw error
    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Generate draft units for a project
 *
 * @param {string} projectId - Project UUID
 * @param {number} count - Number of units to generate
 * @returns {Promise<{error: Object|null}>}
 */
export async function generateDraftUnits(projectId, count) {
  try {
    const { error } = await supabase.rpc('generate_draft_units', {
      p_project_id: projectId,
      p_count: count,
    })

    if (error) throw error
    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}
